import { Badge, Box } from "@mantine/core"
import type { CSSProperties, ReactNode } from "react"
import classes from "./status-pill.module.css"

/**
 * Status tones. Colour = meaning: green done/ok, yellow waiting on someone, red stopped/rejected,
 * blue informational step, gray draft/inactive.
 */
export type StatusTone = "green" | "yellow" | "red" | "blue" | "gray"

/**
 * The one status pill for every module — the same recipe as de-network-admin `StatusBadge`
 * («● Зөвшөөрсөн»): Mantine Badge light + 6px dot, pale tinted background, text #334155 (the
 * @dehub/theme Badge override for light variants). Colour lives only in the dot and the background.
 * Never add other greens (teal, lime, green.7…) for a status: everything positive is `green`.
 */
export function StatusPill({ tone, children }: Readonly<{ tone: StatusTone; children: ReactNode }>) {
  return (
    <Badge
      variant="light"
      color={tone}
      radius="xl"
      tt="none"
      classNames={{ root: classes.badge }}
      style={{ "--badge-dot": `var(--mantine-color-${tone}-filled)` } as CSSProperties}
      leftSection={<Box component="span" className={classes.badgeDot} />}>
      {children}
    </Badge>
  )
}

/**
 * Catalog / legacy colours (Mantine names, `var(--mantine-color-x-n)`, `x.n`, hex) → a status tone.
 * Backend catalogs (ORDER_STATUS, delivery-note status, payment status…) send their own colour per
 * status; the pill only has five tones so every module shows the same green, red, …
 */
export function toneOfColor(color?: string | null): StatusTone {
  const c = (color ?? "").toLowerCase()
  if (/green|teal|lime/.test(c)) return "green"
  if (/red|pink/.test(c)) return "red"
  if (/yellow|orange|amber/.test(c)) return "yellow"
  if (/blue|violet|indigo|cyan|grape/.test(c)) return "blue"
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(c)
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split("").map((d) => d + d).join("") : hex[1]
    const n = parseInt(h, 16)
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    if (max - min < 24) return "gray"
    if (max === r) return g > 110 ? "yellow" : "red"
    if (max === g) return "green"
    return "blue"
  }
  return "gray"
}
